import {
  Component,
  ComponentFactoryResolver,
  EventEmitter,
  HostListener,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { SearchbarModalComponent } from '@app/shared/components/searchbar/searchbar-modal/searchbar-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { IAppState } from '@app/store';
import { Router, NavigationStart, ActivationEnd } from '@angular/router';
import { SearchbarContentComponent } from '@app/shared/components/searchbar/searchbar-content/searchbar-content.component';
import { WindowRefService } from '@app/shared/services/windowRef/window-ref.service';
import * as fromRouter from '@app/store/actions/router.action';
import * as fromSearch from '@app/store/actions/search.action';
import { PlatformService } from '../../services/platform/platform.service';
import { HelpersService } from '@app/shared/services/helpers/helpers.service';

@Component({
  selector: 'vc-searchbar',
  templateUrl: './searchbar.component.html',
  styleUrls: ['./searchbar.component.scss']
})
export class SearchbarComponent implements OnInit {
  @Output()
  searchActive: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output()
  closeSearch: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ViewChild('searchDesktop', { read: ViewContainerRef, static: false })
  private searchDesktop;

  public searchValue = '';
  public showResetBtn = false;
  public isLgUp = true;
  public isSearchActive = false;
  public isSearchHeaderOpen = false;
  private ref: any;

  constructor(
    public modal: MatDialog,
    private state: Store<IAppState>,
    private router: Router,
    private componentFactoryResolver: ComponentFactoryResolver,
    private winRef: WindowRefService,
    private platformService: PlatformService,
    private helpService: HelpersService
  ) {}

  ngOnInit() {
    if (!this.platformService.isBrowser) {
      return;
    }
    this.searchActive.emit(this.isSearchActive);
    this.isLgUp = this.helpService.device.isLgUp();
    this.subscribeToSearchValue();
    this.registerRoutingEvents();
  }

  @HostListener('window:scroll', [])
  onScroll() {
    if (this.ref && this.isSearchHeaderOpen) {
      const window__scrollTop =
        this.winRef.nativeWindow.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
      if (window__scrollTop > 150) {
        this.closeSearchHeader();
      }
    }
  }

  @HostListener('document:click', ['$event'])
  onClick($event) {
    if (this.ref && this.isSearchHeaderOpen) {
      const searchboxinput = document.querySelector('.ais-SearchBox-input');
      if (
        $event.target !== this.searchDesktop.element.nativeElement &&
        $event.target !== searchboxinput &&
        !$event.target.classList.contains('searchbar__clear--header')
      ) {
        this.closeSearchHeader();
      }
    }
  }

  @HostListener('document:keyup', ['$event'])
  onKeyup($event) {
    if ($event.keyCode === 13) {
      this.closeSearchHeader();
    }
  }

  openSearchHeader(): void {
    this.searchDesktop.clear();
    const factory = this.componentFactoryResolver.resolveComponentFactory(SearchbarContentComponent);
    this.ref = this.searchDesktop.createComponent(factory);
    this.ref.changeDetectorRef.detectChanges();
    this.isSearchHeaderOpen = true;
    const searchinputs: any = document.querySelector('.ais-SearchBox-input') || [];
    searchinputs.focus();
    this.isSearchActive = true;
    this.searchActive.emit(this.isSearchActive);
  }

  // Open searchbar header on modal
  openSearchbarModal(): void {
    const config: any = {
      maxWidth: '100%',
      hasBackdrop: true,
      backdropClass: 'searchModal__backdrop',
      panelClass: 'searchModal__panel',
      position: { top: 0, bottom: 0, left: 0, right: 0 }
    };
    if (!this.isLgUp) {
      const dialogRef = this.modal.open(SearchbarModalComponent, config);
      dialogRef.afterClosed().subscribe(
        result => {
          this.closeSearch.emit(true);
        },
        err => {
          // Silently fails
        }
      );
    }
  }

  // clear search - empty search
  clearSearch(): void {
    this.searchValue = '';
    this.state.dispatch(new fromSearch.SetSearchValue(''));
    this.showResetBtn = false;
    if (this.isSearchHeaderOpen) {
      this.focusSearchInput();
    } else {
      this.searchActive.emit(false);
      this.isSearchHeaderOpen = false;
      this.isSearchActive = false;
    }
  }

  // close search header AND clear search
  leaveSearchHeader(): void {
    this.clearSearch();
    if (this.ref) {
      this.ref.destroy();
    }
    if (this.router.url.indexOf('search') !== -1) {
      this.state.dispatch({ type: fromRouter.BACK });
    }
    this.isSearchActive = false;
    this.searchActive.emit(this.isSearchActive);
  }

  // close search header but not clear search
  closeSearchHeader() {
    this.isSearchHeaderOpen = false;
    if (this.ref) {
      this.ref.destroy();
    }
    if (this.searchValue === '') {
      this.isSearchActive = false;
      this.searchActive.emit(this.isSearchActive);
    }
  }

  subscribeToSearchValue(): void {
    this.state.select('search').subscribe(
      resp => {
        this.searchValue = resp.searchValue;
      },
      () => {
        /* Silently catch exception */
      }
    );
  }

  /**
   * focusSearchInput sets the focus on the search input
   * Please note that for mobile the focus will be set via instanciation of the
   * search modal through the ngAfterViewInit event, thus we don't need to call it here
   * @memberof SearchbarComponent
   */
  focusSearchInput(): void {
    const searchinputs: any = document.querySelector('.ais-SearchBox-input') || [];
    searchinputs.focus();
  }

  /**
   * registerRoutingEvents listens on the routing event and clears or sets the
   * proper search state in the search bar
   * @memberof SearchbarComponent
   */
  registerRoutingEvents(): void {
    this.router.events.subscribe(
      routingEvent => {
        // Clear up search query if navigating outside of search page
        if (routingEvent instanceof NavigationStart && !this.isSearchUri(routingEvent.url)) {
          this.clearSearch();
          return;
        }
        // Set up search query when navigating to search page with existing query
        if (routingEvent instanceof ActivationEnd) {
          const { q } = routingEvent.snapshot.queryParams;
          if (q && q.length > 0) {
            this.state.dispatch({ type: fromSearch.SET_SEARCH_VALUE, payload: q });
          }
          return;
        }
      },
      _ => {}
    );
  }

  /**
   * isSearchUri is a helper function to check if we're on the
   * search location
   * @param {string} uri
   * @returns {boolean}
   * @memberof SearchbarComponent
   */
  isSearchUri(uri: string): boolean {
    return uri.startsWith('/search/');
  }
}
